/**
 * The shell's page card rounds its left corners only when no sidebar sits
 * beside it, and eases that change so collapsing the sidebar reads as the
 * page closing over it (index.css, [data-shell-page]).
 *
 * A sidebar that arrives or leaves with the page is not a collapse: the
 * Apps launcher has none and an app area has one, so entering iManage and
 * coming back used to animate the page's top-left corner from round to
 * square and back while the sidebar was already there, or already gone.
 * This hook reports the render on which the sidebar appears or disappears,
 * so the layout can mark it and the corner changes at once; it settles two
 * frames later, after the new geometry has painted, so a later collapse
 * still eases.
 *
 * @module components/shellSidebarPresence
 */
import { useEffect, useState } from "react";

export function useSidebarPresenceSwitch(hasSidebar: boolean): boolean {
  const [settled, setSettled] = useState(hasSidebar);
  const switching = settled !== hasSidebar;
  useEffect(() => {
    if (!switching) return;
    let second = 0;
    const first = requestAnimationFrame(() => {
      second = requestAnimationFrame(() => setSettled(hasSidebar));
    });
    return () => {
      cancelAnimationFrame(first);
      cancelAnimationFrame(second);
    };
  }, [switching, hasSidebar]);
  return switching;
}
